<template>
  <div class="row mb-4">
    <div class="col-12">
      <div class="pane-header">
        <span class="icon"><icon-colored-daypart /></span>
        <span>{{ capitalize($t('names.lineItem', lineItems.length)) }}</span>
      </div>
    </div>
    <div class="col-12">
      <LineItemsTable
        id="deal_line_items_list_table"
        ref="lineItemsTable"
        :allow-selection="true"
        :filters-changed-callback="onLineItemsTableFiltersChanged"
        :is-loading-line-items="isLoading"
        :line-items-total-count="lineItemsTotalCount"
        :line-items="lineItems"
        :fixed-filters="fixedFilters"
        :show-filters="true"
        :get-action="getLineItems"
        @loading-selection="isLoadingSelections = true"
        @selection-changed="selectedLineItems = $event" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, onMounted, Ref, ref } from 'vue';

  import LineItemsTable from '@/common/components/lineitems/LineItemsTable.vue';
  import { IconColoredDaypart } from '@/common/icons/colored';
  import { LineItem, Package } from '@/common/models';
  import Filter from '@/common/models/query-params/Filter';
  import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
  import { mapActions, mapOrgAppActions } from '@/utils/StateMapping';

  interface IDealDetailsLineItemsProps {
    deal: Package;
  }

  const props = defineProps<IDealDetailsLineItemsProps>();

  const lineItems: Ref<LineItem[]> = ref([]);
  const lineItemsTable = ref(null);
  const isLoading = ref(false);
  const isLoadingSelections = ref(false);
  const lineItemsTotalCount = ref(0);
  const missingCreativeSizes = ref([]);
  const selectedLineItems = ref([]);

  const fixedFilters = [new Filter('package.deal_id', [props.deal.dealID], FilterComparisonOperator.EQUAL)];

  const { getLineItems, getLineItemCreativeMissingDimensions } = mapOrgAppActions();
  const { updateLineItemMissingCreativeSizes } = mapActions();

  function updateLineItemMissingSizes() {
    if (missingCreativeSizes.value === null) return;
    lineItems.value.forEach((li) => {
      const creativeLineItemMissingSizes = missingCreativeSizes.value[li.id];
      if (creativeLineItemMissingSizes?.for_line_item == null) {
        return;
      }
      const dimensions = creativeLineItemMissingSizes.for_line_item.map((x) =>
        x.statuses.filter((y) => y.status === 'BAD').map((y) => y.dimensions),
      );

      const uniqueDimensions = dimensions
        .reduce((mem, x) => {
          const key = `${x[0]},${x[1]}`;
          if (mem.some((m) => `${m[0]},${m[1]}` === key)) {
            return mem;
          }
          return [...mem, x];
        }, [])
        .sort((a, b) => a[0] * 10000 + a[1] - (b[0] * 10000 + b[1]));

      if (uniqueDimensions.length > 0) {
        updateLineItemMissingCreativeSizes({
          lineItem: li,
          value: uniqueDimensions,
        });
      }
    });
  }

  async function fetchLineItemsForPackage(queryParams) {
    try {
      isLoading.value = true;
      const response = await queryParams.run(getLineItems);
      if (response) {
        lineItems.value = response.items;
        lineItemsTotalCount.value = response.totalCount;

        if (lineItemsTotalCount.value > 0) {
          missingCreativeSizes.value = await getLineItemCreativeMissingDimensions(lineItems.value.map((x) => x.id));
          updateLineItemMissingSizes();
        }
      }
    } finally {
      isLoading.value = false;
    }
  }

  function reloadLineItems() {
    if (lineItemsTable.value?.getReferenceToTable() == null) {
      setTimeout(reloadLineItems, 100);
      return;
    }
    const { queryParams } = lineItemsTable.value.getReferenceToTable();
    fetchLineItemsForPackage(queryParams);
  }
  function onLineItemsTableFiltersChanged() {
    reloadLineItems();
  }

  onMounted(() => {
    // Initial fetching of line items
    reloadLineItems();
  });
</script>

<style lang="scss" scoped>
  @use '@/styles/variables.scss' as *;
</style>
